import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Copy, Hand, Pencil, Move } from 'lucide-react';
import { Button } from '../../components/ui/button';

/**
 * ShapeEditor — draw axis-aligned rectangles directly on the canvas.
 *
 * Modes:
 *   - "draw" (default): click-drag on empty canvas to draw a new rectangle
 *   - "edit": existing rectangles are selectable/movable/resizable
 *
 * Rectangles are stored in feet, at an offset relative to user-defined origin.
 * Coordinates use standard SVG convention (Y increases down). The user doesn't
 * interact with X/Y numerically — they see dimensions (W × H) on each shape.
 */

const SNAP_OPTIONS = [
  { value: 0, label: 'Off' },
  { value: 0.25, label: '3″' },
  { value: 0.5, label: '6″' },
  { value: 1, label: '1′' },
];

const snap = (v, step) => (step > 0 ? Math.round(v / step) * step : v);

function newId() {
  return `r_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

export default function ShapeEditor({ rectangles, onChange }) {
  const svgRef = useRef(null);
  const [mode, setMode] = useState('draw'); // 'draw' | 'edit'
  const [snapStep, setSnapStep] = useState(0.5);
  const [selectedId, setSelectedId] = useState(null);
  const [drag, setDrag] = useState(null); // active pointer interaction

  const bounds = useMemo(() => {
    if (!rectangles.length) return { minX: -2, minY: -2, maxX: 22, maxY: 18 };
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const r of rectangles) {
      if (r.x < minX) minX = r.x;
      if (r.y < minY) minY = r.y;
      if (r.x + r.w > maxX) maxX = r.x + r.w;
      if (r.y + r.h > maxY) maxY = r.y + r.h;
    }
    // Pad generously so there's always room to draw beyond
    const padX = Math.max(4, (maxX - minX) * 0.25);
    const padY = Math.max(4, (maxY - minY) * 0.25);
    return { minX: minX - padX, minY: minY - padY, maxX: maxX + padX, maxY: maxY + padY };
  }, [rectangles]);

  const viewBox = `${bounds.minX} ${bounds.minY} ${bounds.maxX - bounds.minX} ${bounds.maxY - bounds.minY}`;

  const shape = useMemo(() => computeUnion(rectangles), [rectangles]);

  // ── Pointer → SVG coord transform ──────────────────────────────
  const toSvg = (e) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const m = svg.getScreenCTM();
    if (!m) return { x: 0, y: 0 };
    const p = pt.matrixTransform(m.inverse());
    return { x: snap(p.x, snapStep), y: snap(p.y, snapStep) };
  };

  // ── Pointer handlers ──────────────────────────────────────────
  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    const { x, y } = toSvg(e);

    // In edit mode, hit-test existing rectangles (topmost first)
    if (mode === 'edit') {
      // Check handles first if a shape is selected
      if (selectedId) {
        const r = rectangles.find(r => r.id === selectedId);
        if (r) {
          const h = hitTestHandle(r, x, y, snapStep);
          if (h) {
            e.currentTarget.setPointerCapture(e.pointerId);
            setDrag({ kind: 'resize', id: r.id, handle: h, start: { x, y }, orig: { ...r } });
            return;
          }
        }
      }
      // Topmost rectangle under cursor
      for (let i = rectangles.length - 1; i >= 0; i--) {
        const r = rectangles[i];
        if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) {
          setSelectedId(r.id);
          e.currentTarget.setPointerCapture(e.pointerId);
          setDrag({ kind: 'move', id: r.id, start: { x, y }, orig: { ...r } });
          return;
        }
      }
      // Empty space → deselect
      setSelectedId(null);
      return;
    }

    // Draw mode → start a new rectangle
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrag({ kind: 'draw', start: { x, y }, current: { x, y } });
  };

  const onPointerMove = (e) => {
    if (!drag) return;
    const { x, y } = toSvg(e);
    if (drag.kind === 'draw') {
      setDrag(d => ({ ...d, current: { x, y } }));
    } else if (drag.kind === 'move') {
      const dx = x - drag.start.x;
      const dy = y - drag.start.y;
      onChange(rectangles.map(r =>
        r.id === drag.id ? { ...r, x: snap(drag.orig.x + dx, snapStep), y: snap(drag.orig.y + dy, snapStep) } : r
      ));
    } else if (drag.kind === 'resize') {
      const o = drag.orig;
      let nx = o.x, ny = o.y, nw = o.w, nh = o.h;
      if (drag.handle.includes('w')) { const dx = x - o.x; nx = snap(x, snapStep); nw = Math.max(snapStep || 0.5, o.w - (nx - o.x)); }
      if (drag.handle.includes('e')) { nw = Math.max(snapStep || 0.5, snap(x - o.x, snapStep)); }
      if (drag.handle.includes('n')) { const dy = y - o.y; ny = snap(y, snapStep); nh = Math.max(snapStep || 0.5, o.h - (ny - o.y)); }
      if (drag.handle.includes('s')) { nh = Math.max(snapStep || 0.5, snap(y - o.y, snapStep)); }
      onChange(rectangles.map(r => r.id === drag.id ? { ...r, x: nx, y: ny, w: nw, h: nh } : r));
    }
  };

  const onPointerUp = (e) => {
    if (!drag) return;
    if (drag.kind === 'draw') {
      const x1 = Math.min(drag.start.x, drag.current.x);
      const y1 = Math.min(drag.start.y, drag.current.y);
      const w = Math.abs(drag.current.x - drag.start.x);
      const h = Math.abs(drag.current.y - drag.start.y);
      // Only commit if it's at least half a snap step in both directions
      const minSize = Math.max(0.25, snapStep || 0.25);
      if (w >= minSize && h >= minSize) {
        const id = newId();
        onChange([...rectangles, { id, x: x1, y: y1, w, h }]);
        setSelectedId(id);
      }
    }
    setDrag(null);
  };

  // ── Keyboard: Delete / Escape ─────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (!selectedId) return;
      // Don't swallow keystrokes while typing in an input
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        onChange(rectangles.filter(r => r.id !== selectedId));
        setSelectedId(null);
      } else if (e.key === 'Escape') {
        setSelectedId(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, rectangles, onChange]);

  const removeRect = useCallback((id) => {
    onChange(rectangles.filter(r => r.id !== id));
    if (selectedId === id) setSelectedId(null);
  }, [rectangles, onChange, selectedId]);

  const duplicateRect = useCallback((id) => {
    const r = rectangles.find(r => r.id === id);
    if (!r) return;
    const clone = { ...r, id: newId(), x: r.x + 1, y: r.y + 1 };
    onChange([...rectangles, clone]);
    setSelectedId(clone.id);
  }, [rectangles, onChange]);

  const clearAll = () => {
    if (!rectangles.length) return;
    if (!confirm('Clear all rectangles?')) return;
    onChange([]);
    setSelectedId(null);
  };

  // ── Drawing preview while dragging ────────────────────────────
  let preview = null;
  if (drag?.kind === 'draw') {
    const px = Math.min(drag.start.x, drag.current.x);
    const py = Math.min(drag.start.y, drag.current.y);
    const pw = Math.abs(drag.current.x - drag.start.x);
    const ph = Math.abs(drag.current.y - drag.start.y);
    preview = { x: px, y: py, w: pw, h: ph };
  }

  const cursor = mode === 'draw' ? 'crosshair' : drag?.kind === 'move' ? 'grabbing' : 'default';
  const handleSize = Math.max(0.15, (bounds.maxX - bounds.minX) * 0.008);

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap bg-[#F9F9F8] border border-[#E6EBE8] rounded-md p-2">
        <div className="inline-flex rounded-md border border-[#E6EBE8] bg-white overflow-hidden">
          <button
            onClick={() => setMode('draw')}
            data-testid="mode-draw"
            className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 ${
              mode === 'draw' ? 'bg-[#2D6B68] text-white' : 'text-[#1A1D1C] hover:bg-[#F0F3F1]'
            }`}
          >
            <Pencil className="w-3.5 h-3.5" /> Draw
          </button>
          <button
            onClick={() => setMode('edit')}
            data-testid="mode-edit"
            className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 ${
              mode === 'edit' ? 'bg-[#2D6B68] text-white' : 'text-[#1A1D1C] hover:bg-[#F0F3F1]'
            }`}
          >
            <Hand className="w-3.5 h-3.5" /> Edit
          </button>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-[#6B7A73]">Snap:</span>
          <div className="inline-flex rounded-md border border-[#E6EBE8] bg-white overflow-hidden">
            {SNAP_OPTIONS.map(s => (
              <button
                key={s.value}
                onClick={() => setSnapStep(s.value)}
                data-testid={`snap-${s.label}`}
                className={`px-2.5 py-1 text-[11px] font-medium ${
                  snapStep === s.value ? 'bg-[#2D6B68] text-white' : 'text-[#1A1D1C] hover:bg-[#F0F3F1]'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-1">
          {selectedId && (
            <>
              <Button onClick={() => duplicateRect(selectedId)} variant="outline" size="sm" className="h-7 text-xs border-[#E6EBE8]" data-testid="duplicate-selected">
                <Copy className="w-3 h-3 mr-1" /> Duplicate
              </Button>
              <Button onClick={() => removeRect(selectedId)} variant="outline" size="sm" className="h-7 text-xs border-red-200 text-red-600 hover:bg-red-50">
                <Trash2 className="w-3 h-3" />
              </Button>
            </>
          )}
          <Button onClick={clearAll} variant="outline" size="sm" className="h-7 text-xs border-[#E6EBE8]" disabled={rectangles.length === 0}>
            Clear
          </Button>
        </div>
      </div>

      {/* Canvas */}
      <div className="bg-[#F9FAFA] border border-[#E6EBE8] rounded-md p-0 overflow-hidden">
        {rectangles.length === 0 && !drag && (
          <div className="absolute z-10 pointer-events-none px-4 py-2 m-3 bg-white/90 border border-[#E6EBE8] rounded-md text-xs text-[#6B7A73] shadow-sm">
            <Move className="w-3 h-3 inline mr-1" />
            Click and drag anywhere to draw your first rectangle
          </div>
        )}
        <svg
          ref={svgRef}
          viewBox={viewBox}
          className="w-full h-80 touch-none select-none"
          preserveAspectRatio="xMidYMid meet"
          style={{ cursor }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          data-testid="shape-svg"
        >
          <defs>
            <pattern id="grid-minor" width={snapStep > 0 ? snapStep : 0.5} height={snapStep > 0 ? snapStep : 0.5} patternUnits="userSpaceOnUse">
              <path d={`M ${snapStep > 0 ? snapStep : 0.5} 0 L 0 0 0 ${snapStep > 0 ? snapStep : 0.5}`} fill="none" stroke="#D1D5D3" strokeWidth="0.02" />
            </pattern>
            <pattern id="grid-foot" width="1" height="1" patternUnits="userSpaceOnUse">
              <path d="M 1 0 L 0 0 0 1" fill="none" stroke="#A8B0AC" strokeWidth="0.03" />
            </pattern>
            <pattern id="grid-major" width="5" height="5" patternUnits="userSpaceOnUse">
              <path d="M 5 0 L 0 0 0 5" fill="none" stroke="#6B7A73" strokeWidth="0.04" />
            </pattern>
          </defs>
          <rect x={bounds.minX} y={bounds.minY} width={bounds.maxX - bounds.minX} height={bounds.maxY - bounds.minY} fill="url(#grid-minor)" />
          <rect x={bounds.minX} y={bounds.minY} width={bounds.maxX - bounds.minX} height={bounds.maxY - bounds.minY} fill="url(#grid-foot)" />
          <rect x={bounds.minX} y={bounds.minY} width={bounds.maxX - bounds.minX} height={bounds.maxY - bounds.minY} fill="url(#grid-major)" />

          {/* Rectangles */}
          {rectangles.map((r, i) => {
            const isSelected = r.id === selectedId;
            // Show persistent dimension guides on the selected rect, and
            // also while it's actively being moved / resized.
            const showDims = isSelected || (drag && drag.id === r.id);
            return (
              <g key={r.id}>
                <rect
                  x={r.x}
                  y={r.y}
                  width={r.w}
                  height={r.h}
                  fill="#2D6B68"
                  fillOpacity={isSelected ? 0.45 : 0.3}
                  stroke={isSelected ? '#1A1D1C' : '#2D6B68'}
                  strokeWidth={isSelected ? 0.08 : 0.05}
                  data-testid={`rect-${i}`}
                />
                <text
                  x={r.x + r.w / 2}
                  y={r.y + r.h / 2}
                  fontSize="0.5"
                  fill="#1A1D1C"
                  textAnchor="middle"
                  fontWeight="bold"
                  pointerEvents="none"
                >
                  #{i + 1}
                </text>
                <text
                  x={r.x + r.w / 2}
                  y={r.y + r.h / 2 + 0.7}
                  fontSize="0.45"
                  fill="#1A1D1C"
                  textAnchor="middle"
                  pointerEvents="none"
                >
                  {fmtDim(r.w)} × {fmtDim(r.h)}
                </text>
                {showDims && <DimensionGuides rect={r} />}
                {/* Selected → draw resize handles */}
                {isSelected && mode === 'edit' && (
                  <g pointerEvents="none">
                    {CORNER_HANDLES.map(h => {
                      const cx = h.includes('w') ? r.x : r.x + r.w;
                      const cy = h.includes('n') ? r.y : r.y + r.h;
                      return (
                        <rect
                          key={h}
                          x={cx - handleSize / 2}
                          y={cy - handleSize / 2}
                          width={handleSize}
                          height={handleSize}
                          fill="#fff"
                          stroke="#1A1D1C"
                          strokeWidth="0.04"
                        />
                      );
                    })}
                  </g>
                )}
              </g>
            );
          })}

          {/* Drawing preview */}
          {preview && (
            <g pointerEvents="none">
              <rect
                x={preview.x}
                y={preview.y}
                width={preview.w}
                height={preview.h}
                fill="#2D6B68"
                fillOpacity="0.2"
                stroke="#2D6B68"
                strokeWidth="0.08"
                strokeDasharray="0.2 0.15"
              />
              {preview.w > 0.2 && preview.h > 0.2 && (
                <DimensionGuides rect={preview} />
              )}
            </g>
          )}
        </svg>
      </div>

      {/* Summary */}
      <div className="bg-[#E5F0EE] border border-[#2D6B68]/30 rounded-md p-3 flex items-center justify-between text-sm flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#2D6B68] font-semibold">Combined Area (overlaps merged)</p>
          <p className="text-lg font-bold text-[#1A1D1C]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}
             data-testid="combined-area">
            {shape.area_ft2.toFixed(2)} ft²
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider text-[#6B7A73] font-semibold">Outer Perimeter</p>
          <p className="text-base font-bold text-[#1A1D1C]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}
             data-testid="combined-perimeter">
            {shape.perimeter_ft.toFixed(1)} ft
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider text-[#6B7A73] font-semibold">Count</p>
          <p className="text-base font-bold text-[#1A1D1C]">{rectangles.length}</p>
        </div>
      </div>
      <p className="text-[10px] text-[#6B7A73] italic">
        Tip: <strong>Draw</strong> mode — click and drag to add shapes. <strong>Edit</strong> mode — click to select, drag to move, drag a corner to resize. Press <strong>Delete</strong> to remove selected.
      </p>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────

const CORNER_HANDLES = ['nw', 'ne', 'sw', 'se'];

/**
 * Blueprint-style dimension guides drawn outside the rectangle:
 *   W label above the top edge, H label on the left edge, with arrow ticks.
 */
function DimensionGuides({ rect }) {
  const { x, y, w, h } = rect;
  const offW = 0.6; // distance above top
  const offH = 0.6; // distance left of left edge
  const tick = 0.25;
  const fontSize = 0.45;
  const bg = '#FFF9E5';
  const fg = '#8A5A00';
  const padX = 0.3;
  const padY = 0.12;

  const widthText = fmtDim(w);
  const heightText = fmtDim(h);
  // Rough width estimate for the background pill
  const wTextW = Math.max(0.8, widthText.length * fontSize * 0.55);
  const hTextW = Math.max(0.8, heightText.length * fontSize * 0.55);

  return (
    <g pointerEvents="none" data-testid="dim-guides">
      {/* Top — width */}
      <line x1={x} y1={y - offW} x2={x + w} y2={y - offW} stroke={fg} strokeWidth="0.035" />
      <line x1={x} y1={y - offW - tick} x2={x} y2={y - offW + tick} stroke={fg} strokeWidth="0.035" />
      <line x1={x + w} y1={y - offW - tick} x2={x + w} y2={y - offW + tick} stroke={fg} strokeWidth="0.035" />
      <rect
        x={x + w / 2 - wTextW / 2 - padX}
        y={y - offW - fontSize / 2 - padY}
        width={wTextW + padX * 2}
        height={fontSize + padY * 2}
        rx="0.08"
        fill={bg}
        stroke={fg}
        strokeWidth="0.02"
      />
      <text
        x={x + w / 2}
        y={y - offW + fontSize * 0.32}
        fontSize={fontSize}
        fill={fg}
        textAnchor="middle"
        fontWeight="bold"
      >
        {widthText}
      </text>

      {/* Left — height */}
      <line x1={x - offH} y1={y} x2={x - offH} y2={y + h} stroke={fg} strokeWidth="0.035" />
      <line x1={x - offH - tick} y1={y} x2={x - offH + tick} y2={y} stroke={fg} strokeWidth="0.035" />
      <line x1={x - offH - tick} y1={y + h} x2={x - offH + tick} y2={y + h} stroke={fg} strokeWidth="0.035" />
      <rect
        x={x - offH - hTextW / 2 - padX}
        y={y + h / 2 - fontSize / 2 - padY}
        width={hTextW + padX * 2}
        height={fontSize + padY * 2}
        rx="0.08"
        fill={bg}
        stroke={fg}
        strokeWidth="0.02"
      />
      <text
        x={x - offH}
        y={y + h / 2 + fontSize * 0.32}
        fontSize={fontSize}
        fill={fg}
        textAnchor="middle"
        fontWeight="bold"
      >
        {heightText}
      </text>
    </g>
  );
}

function hitTestHandle(r, x, y, snapStep) {
  const handleRadius = Math.max(0.3, (snapStep || 0.5) * 1.2);
  const corners = {
    nw: [r.x, r.y],
    ne: [r.x + r.w, r.y],
    sw: [r.x, r.y + r.h],
    se: [r.x + r.w, r.y + r.h],
  };
  for (const [k, [cx, cy]] of Object.entries(corners)) {
    if (Math.abs(x - cx) <= handleRadius && Math.abs(y - cy) <= handleRadius) return k;
  }
  return null;
}

function fmtDim(ft) {
  // Render as `8' 6"` if non-integer-feet, else `8'`
  const whole = Math.floor(ft);
  const inches = Math.round((ft - whole) * 12);
  if (inches === 0) return `${whole}′`;
  if (whole === 0) return `${inches}″`;
  return `${whole}′ ${inches}″`;
}

export function boundingBox(rects) {
  if (!rects.length) return { minX: 0, minY: 0, maxX: 10, maxY: 10 };
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const r of rects) {
    if (r.x < minX) minX = r.x;
    if (r.y < minY) minY = r.y;
    if (r.x + r.w > maxX) maxX = r.x + r.w;
    if (r.y + r.h > maxY) maxY = r.y + r.h;
  }
  return { minX, minY, maxX, maxY };
}

export function computeUnion(rects) {
  if (!rects.length) return { area_ft2: 0, perimeter_ft: 0 };
  const b = boundingBox(rects);
  const w = b.maxX - b.minX;
  const h = b.maxY - b.minY;
  const cellSize = Math.max(0.25, Math.max(w, h) / 320);
  const cols = Math.max(1, Math.ceil(w / cellSize));
  const rows = Math.max(1, Math.ceil(h / cellSize));
  const grid = new Uint8Array(cols * rows);
  for (const r of rects) {
    const x0 = Math.max(0, Math.floor((r.x - b.minX) / cellSize));
    const y0 = Math.max(0, Math.floor((r.y - b.minY) / cellSize));
    const x1 = Math.min(cols, Math.ceil((r.x + r.w - b.minX) / cellSize));
    const y1 = Math.min(rows, Math.ceil((r.y + r.h - b.minY) / cellSize));
    for (let yy = y0; yy < y1; yy++) {
      for (let xx = x0; xx < x1; xx++) grid[yy * cols + xx] = 1;
    }
  }
  let inside = 0, peri = 0;
  for (let i = 0; i < grid.length; i++) if (grid[i]) inside++;
  for (let yy = 0; yy < rows; yy++) {
    for (let xx = 0; xx < cols; xx++) {
      if (!grid[yy * cols + xx]) continue;
      if (yy === 0 || !grid[(yy - 1) * cols + xx]) peri++;
      if (yy === rows - 1 || !grid[(yy + 1) * cols + xx]) peri++;
      if (xx === 0 || !grid[yy * cols + (xx - 1)]) peri++;
      if (xx === cols - 1 || !grid[yy * cols + (xx + 1)]) peri++;
    }
  }
  return { area_ft2: inside * cellSize * cellSize, perimeter_ft: peri * cellSize };
}

// Back-compat export: legacy import name
export const computeShape = computeUnion;
